import { resolveApiUrl } from '../utils/core/endpoints';
import { requestS3URL, requestS3URLBinary, requestS3URLText } from './web-api-client';

export class WebApi {
  static async fetchS3DataAsArrayBuffer(url: string): Promise<ArrayBuffer> {
    const response = await requestS3URLBinary(url);
    return response.data;
  }

  static async fetchS3Data(url: string) {
    const response = await requestS3URL(url);
    return response.data;
  }

  static async fetchS3DataSafe(url: string) {
    try {
      const response = await requestS3URL(url);
      return response.data;
    } catch (err) {
      return undefined;
    }
  }

  static getUrl(): string {
    return resolveApiUrl();
  }

  static async fetchS3DataAsText(
    url: string,
    options?: {
      cacheControl?: 'no-cache';
      onProgress?: (event: { loaded: number; total?: number }) => void;
    },
  ): Promise<string> {
    const response = await requestS3URLText(url, {
      onProgress: options?.onProgress
        ? (event) => {
            options.onProgress?.({
              loaded: event.loaded,
              total: event.total,
            });
          }
        : undefined,
    });
    return response.data;
  }

  /**
   * Puts the whole file to the target URL.
   * Fast and efficient for small to medium sized files but avoid using it for large files.
   */
  static async put(url: string, file: File): Promise<void> {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error('Upload failed with status: ' + response.status);
    }
  }

  static async delete(url: string): Promise<void> {
    const response = await fetch(url, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Deletion failed with status: ' + response.status);
    }
  }

  static async putFileToS3Multipart(
    url: string,
    file: File,
    onProgress?: (event: ProgressEvent) => void,
  ): Promise<void> {
    const chunkSize = 1024 * 1024; // 1 MB chunk size, adjust as needed
    const totalChunks = Math.ceil(file.size / chunkSize);
    const parts = [];

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const chunk = file.slice(start, end);

      const requestOptions = {
        method: 'PUT',
        body: chunk,
        headers: {
          'Content-Type': file.type,
          'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
        },
      };

      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        throw new Error('Upload failed with status: ' + response.status);
      }

      const etag = response.headers.get('ETag');
      parts.push({ PartNumber: i + 1, ETag: etag });

      // Optionally, call onProgress if provided
      if (onProgress) {
        const progressEvent = { loaded: end, total: file.size } as ProgressEvent;
        onProgress(progressEvent);
      }
    }

    // Complete multipart upload
    const completeUrl = `${url}?uploads`; // Assuming the URL already includes the necessary query parameters
    const completeRequestOptions = {
      method: 'POST',
      body: JSON.stringify({ parts }),
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const completeResponse = await fetch(completeUrl, completeRequestOptions);
    if (!completeResponse.ok) {
      throw new Error('Failed to complete multipart upload: ' + completeResponse.status);
    }
  }
}
